import { Link } from 'react-router-dom';
import { PlayCircle } from 'lucide-react';

/**
 * The "Weiterlernen" card: thumb, kicker, lesson title, one line of context,
 * and a hairline progress bar along the bottom.
 *
 * Purely presentational, and shared on purpose — Home offers it for the most
 * recently touched lesson across all courses, the course page for the first
 * incomplete lesson of that one course. Two different questions, but the same
 * affordance to the learner, so they must not drift into two patterns.
 */
export function ResumeCard({
  to,
  title,
  subtitle,
  thumbnailUrl,
  pct,
}: {
  to: string;
  title: string;
  /** One line of context — the course on Home, the module on a course page. */
  subtitle: string;
  thumbnailUrl: string | null;
  /** 0–100, how far into this lesson the learner already is. */
  pct: number;
}) {
  return (
    <Link
      to={to}
      className="block overflow-hidden rounded-xl border bg-card text-card-foreground shadow-soft transition duration-200 hover:border-primary/40 hover:shadow-lift"
    >
      <div className="flex items-center gap-4 p-4">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="h-16 w-24 shrink-0 rounded-lg object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-lg bg-muted">
            <PlayCircle className="size-6 text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-xs font-medium uppercase tracking-wide text-primary-strong">
            Weiterlernen
          </div>
          <div className="line-clamp-2 text-sm font-semibold leading-snug">{title}</div>
          <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
        </div>
      </div>
      <div className="h-1 w-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
    </Link>
  );
}
